import { SuccessCallback } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { CardHeaders } from '../../userProfile/src/domain/UserInfo/entities';
import { Card as CardResponse } from './domain/Card/entities/Card';
import { CardDetails as CardDetailsResponse } from './domain/Card/entities/CardDetails';
import { CardTransaction as CardTransactionResponse } from './domain/Card/entities/CardTransaction';
import { CardTransactionListFilters } from './contexts/CardDetails';

export type Filters = Nullable<
  Partial<{
    pageSize?: number;
    pageOffset?: number;
    resultSize?: string;
    allEntries?: string;
    dateFrom?: string;
    dateTo?: string;
    amount?: string;
    rrn?: string;
    merchant?: string;
  }>
>;

export enum CardStatuses {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Blocked = 'BLOCKED',
  Lost = 'LOST',
  Stolen = 'STOLEN',
  Fraud = 'FRAUD',
  Closed = 'CLOSED',
  In_Progress = 'IN_PROGRESS',
  Not_Received = 'NOT_RECEIVED',
  Damaged = 'DAMAGED',
  Pin_Tries_Exceeded = 'PIN_TRIES_EXCEEDED',
}

export enum CardFilterStatuses {
  'ACTIVE' = 1,
  'INACTIVE',
  'IN_PROGRESS',
  'BLOCKED',
  'LOST',
  'STOLEN',
  'FRAUD',
  'CLOSED',
  'NOT_RECEIVED',
  'DAMAGED',
  'PIN_TRIES_EXCEEDED',
}

export type CardStatus = Partial<Record<keyof typeof CardStatuses, string>>;

//Card Domain Types
export enum CardType {
  PREPAID_CARD = 'PrepaidCard',
}

export enum IssueCardStepIdEnum {
  PRODUCT_SELECTOR = 'PRODUCT_SELECTOR',
  ACCOUNT_SELECTOR = 'ACCOUNT_SELECTOR',
  USER_SELECTOR = 'PARTY_SELECTOR',
  VERIFY = 'VERIFY',
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  CARD_ISSUE_REQUEST_END = 'CARD_ISSUE_REQUEST_END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum TopupCardStepIdEnum {
  FROM_ACCOUNT_AND_AMOUNT = 'FROM_ACCOUNT_AND_AMOUNT',
  PAYMENT_CARD_EXTERNAL = 'PAYMENT_CARD_EXTERNAL',
  VERIFY = 'VERIFY',
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum TerminateCardStepIdEnum {
  ACCOUNT_SELECTOR = 'ACCOUNT_SELECTOR',
  VERIFY = 'VERIFY',
  VERIFY_NO_FEES = 'VERIFY_NO_FEES',
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum ReplaceCardStepIdEnum {
  CARD_TERMINATE_REASON = 'CARD_TERMINATE_REASON',
  ACCOUNT_SELECTOR = 'ACCOUNT_SELECTOR',
  VERIFY = 'VERIFY',
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum PermanentBlockCardStepIdEnum {
  CARD_TERMINATE_REASON = 'CARD_TERMINATE_REASON',
  ACCOUNT_SELECTOR = 'ACCOUNT_SELECTOR',
  VERIFY = 'VERIFY',
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum ActivateCardStepIdEnum {
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum ConsentCardStepIdEnum {
  VERIFY = 'VERIFY',
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum CardPinUnblockStepIdEnum {
  AUTHORIZATION = 'AUTHORIZATION',
  END = 'END',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum CustomerTypeEnum {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
}

export enum CardProductTypeEnum {
  PERSONAL_CORPORATE = 'PERSONAL_CORPORATE',
  PERSONAL_INDIVIDUAL = 'PERSONAL_INDIVIDUAL',
  INFINITE_CORPORATE = 'INFINITE_CORPORATE',
  INFINITE_INDIVIDUAL = 'INFINITE_INDIVIDUAL',
}

export enum TopupValidationIdentifier {
  ERROR = 'ERROR',
  SCA_FAILED = 'SCA_FAILED',
  INSUFFICIENT_AMOUNT = 'INSUFFICIENT_AMOUNT',
  CARD_TOP_UP_EXECUTE_FAILED = 'CARD_TOP_UP_EXECUTE_FAILED',
}

export enum TerminateValidationIdentifier {
  ERROR = 'ERROR',
  PCJ_990 = 'PCJ_990',
  SCA_FAILED = 'SCA_FAILED',
  IN_PROGRESS_CARD = 'IN_PROGRESS_CARD',
}

export enum PermanentBlockValidationIdentifier {
  ERROR = 'ERROR',
  PCJ_990 = 'PCJ_990',
  SCA_FAILED = 'SCA_FAILED',
  IN_PROGRESS_CARD = 'IN_PROGRESS_CARD',
}

export enum ReplaceValidationIdentifier {
  ERROR = 'ERROR',
  PCJ_990 = 'PCJ_990',
  SCA_FAILED = 'SCA_FAILED',
  IN_PROGRESS_CARD = 'IN_PROGRESS_CARD',
}

export enum IssueValidationIdentifier {
  ERROR = 'ERROR',
  PRG_400 = 'PRG_400',
  SCA_FAILED = 'SCA_FAILED',
  CARD_ISSUE_VERIFY_FAILED = 'CARD_ISSUE_VERIFY_FAILED',
  CARD_ISSUE_EXECUTE_FAILED = 'CARD_ISSUE_EXECUTE_FAILED',
}

export enum ActivateValidationIdentifier {
  ERROR = 'ERROR',
  PCJ_990 = 'PCJ_990',
  SCA_FAILED = 'SCA_FAILED',
  IN_PROGRESS_CARD = 'IN_PROGRESS_CARD',
}

export enum ConsentValidationIdentifier {
  ERROR = 'ERROR',
  PCJ_990 = 'PCJ_990',
  SCA_FAILED = 'SCA_FAILED',
  IN_PROGRESS_CARD = 'IN_PROGRESS_CARD',
}

export enum CardPinUnblockIdentifier {
  ERROR = 'ERROR',
  PCJ_990 = 'PCJ_990',
  SCA_FAILED = 'SCA_FAILED',
  IN_PROGRESS_CARD = 'IN_PROGRESS_CARD',
}

export enum LiteralTypeEnum {
  DETAILS = 'details',
}

export enum CardAssetSizeEnum {
  SMALL_MODEL = 'small-model',
  SMALL = 'small',
  MEDIUM = 'medium',
  BIG = 'big',
}

export enum CardBalanceTypeEnum {
  BLOCKED_BALANCE = 'BlockedBalance',
  AVAILABLE_BALANCE = 'AvailableBalance',
}

export type CardBalanceProp = {
  value: number;
  decimalPointPosition: Nullable<number>;
  currency: string;
};

export type CardBalance = {
  balance: CardBalanceProp;
  type: CardBalanceTypeEnum;
  dateTime: Nullable<string>;
};

export type PaymentCardIdentifier = {
  value: string;
  issuingAuthority: string;
  uniqueId: Nullable<string | number>;
  startDate: Nullable<string>;
  endDate: Nullable<string>;
};

export type PaymentCardIdentification = {
  type: string;
  identifier: PaymentCardIdentifier;
};

export type FeeCharges = {
  value?: number;
  currency: string;
};

export type CardProductIdentification = {
  type: string;
  identifier: PaymentCardIdentifier;
};

export type CardProductAssets = {
  contentType: string;
  description: string;
  name: string;
  path: string;
  type: string;
};

export type CardProduct = {
  assets: CardProductAssets[];
  identifications: CardProductIdentification[];
  title: string;
};

export type CardName = {
  companyName: string;
  holderName: string;
};

export type CardDetails = {
  paymentCardIdentifications: PaymentCardIdentification[];
  partyIdentifications: PartyIdentification[];
  type: CardType;
  maskedCardNumber: string;
  startDate: string;
  expiryDate: string;
  virtual: boolean;
  balances: CardBalance[];
  status: CardStatuses;
  cardProduct: CardProduct;
  cardNetwork: 'VISA' | 'MASTERCARD';
  cardHolderName: string;
  cardName: CardName;
};

export type IssueCard = {
  paymentCardIdentifications: PaymentCardIdentification[];
  type: CardType;
  maskedCardNumber: string;
  startDate: string;
  expiryDate: string;
  virtual: boolean;
  balances: CardBalance[];
  status: CardStatuses;
  cardProduct: CardProduct;
  cardNetwork: 'VISA' | 'MASTERCARD';
};

export type CardListPagination = {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
  first: boolean;
  last: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
};

export type CardIdentifier = {
  value: string;
  issuingAuthority: string;
};

export type PartyIdentification = {
  type: string;
  identifier: CardIdentifier;
};

export type CardInfoListFilter = {
  paymentCardStatuses?: CardStatuses[];
  cardNumberSuffix?: string;
};

export type CardInfoListPagination = {
  pageNumber: number;
  pageSize: number;
};

export type CardInfoListRequestBody = {
  headers: CardHeaders;
  customerId: string;
  filter?: CardInfoListFilter;
  pagination: {
    pageNumber: number;
    pageSize: number;
  };
};

export type CorporateCardInfoListRequestBody = {
  headers: CardHeaders;
  customerId: string;
  filter?: CardInfoListFilter;
  pagination: {
    pageNumber: number;
    pageSize: number;
  };
};

export type CardInfoListResponseBody = {
  cards: CardResponse[];
  pagination: CardListPagination;
};

export type CardDetailsRequestBody = {
  headers: CardHeaders;
  customerId: string;
  paymentCardIdentifications: PartyIdentification[];
  filter?: CardInfoListFilter;
  nextCallback: SuccessCallback;
};

export type CardTransactionsResponseBody = {
  transactions: CardTransactionResponse[];
  pagination: CardListPagination;
};

export type CardTransactionsRequestBody = {
  headers: CardHeaders;
  customerId: string;
  paymentCardIdentifications: PartyIdentification[];
  filters: CardTransactionListFilters;
};

export enum TopUpSourceEnum {
  OWN_ACCOUNT = 'OWN_ACCOUNT',
  EXTERNAL_CARD = 'EXTERNAL_CARD',
}

export type PaymentInfo = {
  paymentId: string;
  paymentAmount: number;
  paymentCurrency: string;
  paymentDescription: string;
  projectId: string;
  customerId: string;
  languageCode: string;
  customerLastName: string;
  customerFirstName: string;
  customerCity: string;
  customerCountry: string;
  customerEmail: string;
  customerAddress: string;
  recipientWalletId: string;
  recipientWalletOwner: string;
};

export type CardDetailsResponseBody = CardDetailsResponse;

export type CardTermsRequestBody = {
  fileName: string;
};

export enum FormConfigKeyIdentifier {
  ID = 'id',
  CITY = 'city',
  STATE = 'state',
  AMOUNT = 'amount',
  ADDRESS = 'address',
  COUNTRY = 'country',
  NATIONALITY = 'nationality',
  FROM_ACCOUNT_DETAILS = 'fromAccountDetails',
  DELIVERY_ADDRESS = 'deliveryAddress',
  RESIDENCE_ADDRESS = 'residenceAddress',
  BIRTHDATE = 'birthDate',
  FAMILY_NAME = 'familyName',
  FIRST_NAME = 'firstName',
  FULL_NAME = 'fullName',
  DELIVERY_FULL_NAME = 'deliveryFullName',
  LAST_NAME = 'lastName',
  FATHER_NAME = 'fatherName',
  ZIP_CODE = 'zipCode',
  MOBILE_ADDRESS = 'MobileAddress',
  PHONE_ADDRESS = 'PhoneAddress',
  ELECTRONIC_ADDRESS = 'ElectronicAddress',
  IDENTITY_NUMBER = 'IdentityCardNumber',
  PASSPORT = 'PassportNumber',
  REASON = 'cardTerminateReason',
  LOST = 'lost',
  STOLEN = 'stolen',
  DAMAGED = 'damaged',
  NOT_RECEIVED = 'not_received',
  OTHER = 'other',
  OWN_ACCOUNT = 'OWN_ACCOUNT',
  EXTERNAL_CARD = 'EXTERNAL_CARD',
  ACCOUNT_TYPE = 'accountType',
  TERMS = 'termsAndConditions',
}

export enum CardTerminateReasonEnum {
  LOST = 'LOST',
  STOLEN = 'STOLEN',
  DAMAGED = 'DAMAGED',
  OTHER = 'OTHER',
  NOT_RECEIVED = 'NOT_RECEIVED',
}
